import { fmtInt, t } from '../i18n/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CMD, CMD_NAMES, type StitchSequence } from '../engine/stitch/sequence';
import { formatDuration } from '../engine/stitch/stats';
import { store, useApp } from '../state/store';
import { zoomToFit } from './actions';
import { FABRIC, THREAD_WIDTH_MM, buildStitchPaths, clearCanvas, drawStitchPaths, setViewTransform } from './canvas/render';
import { IconPause, IconPlay, IconStepBack, IconStepFwd } from './icons';
import { TouchGesture } from './canvas/touch';
import { wheelZoomFactor, zoomAt } from './canvas/zoom';
import { ZoomControls } from './canvas/ZoomControls';

const SPEEDS = [10, 50, 200, 1000, 5000, 20000];

interface Timeline {
  blocks: { start: number; end: number; color: string }[];
  trims: number[];
  changes: number[];
  /** stitchesBefore[i] = number of STITCH commands in [0, i) */
  stitchesBefore: Int32Array;
}

function buildTimeline(seq: StitchSequence): Timeline {
  const blocks: Timeline['blocks'] = [];
  const trims: number[] = [];
  const changes: number[] = [];
  const stitchesBefore = new Int32Array(seq.count + 1);
  let start = 0;
  for (let i = 0; i < seq.count; i++) {
    stitchesBefore[i + 1] = stitchesBefore[i] + (seq.cmds[i] === CMD.STITCH ? 1 : 0);
    if (seq.cmds[i] === CMD.TRIM) trims.push(i);
    if (seq.cmds[i] === CMD.COLOR_CHANGE) {
      changes.push(i);
      blocks.push({ start, end: i, color: seq.threads[seq.blocks[Math.max(0, i - 1)]]?.hex ?? '#888' });
      start = i;
    }
  }
  if (seq.count) blocks.push({ start, end: seq.count, color: seq.threads[seq.blocks[seq.count - 1]]?.hex ?? '#888' });
  return { blocks, trims, changes, stitchesBefore };
}

export function Simulator() {
  const result = useApp((s) => s.stitch.result);
  const pending = useApp((s) => s.stitch.pending);
  const design = useApp((s) => s.history.present);
  const v = useApp((s) => s.viewport);
  const showImage = useApp((s) => s.layers.image);
  const image = useApp((s) => s.image);
  const seq = result?.sequence ?? null;
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layerRef = useRef<HTMLCanvasElement | null>(null);
  const drawn = useRef<{ upTo: number; key: string }>({ upTo: -1, key: '' });
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [showJumps, setShowJumps] = useState(true);
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);

  const timeline = useMemo(() => (seq ? buildTimeline(seq) : null), [seq]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // new sequence: show the complete design
  useEffect(() => {
    setPos(seq ? seq.count : 0);
    setPlaying(false);
    drawn.current = { upTo: -1, key: '' };
  }, [seq]);

  useEffect(() => {
    if (!image) return setImgEl(null);
    const el = new Image();
    el.onload = () => setImgEl(el);
    el.src = image.url;
  }, [image]);

  // playback
  useEffect(() => {
    if (!playing || !seq) return;
    let raf = 0;
    let last = performance.now();
    let carry = 0;
    const tick = (t: number) => {
      carry += ((t - last) / 1000) * speed;
      last = t;
      const step = Math.floor(carry);
      carry -= step;
      if (step > 0) {
        setPos((p) => {
          const n = Math.min(seq.count, p + step);
          if (n >= seq.count) setPlaying(false);
          return n;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, seq]);

  // drawing: incremental on an offscreen layer, full redraw when going back or when the view changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size.w) return;
    const dpr = window.devicePixelRatio || 1;
    const W = Math.round(size.w * dpr), H = Math.round(size.h * dpr);
    if (canvas.width !== W || canvas.height !== H) {
      canvas.width = W;
      canvas.height = H;
    }
    if (!layerRef.current) layerRef.current = document.createElement('canvas');
    const layer = layerRef.current;
    if (layer.width !== W || layer.height !== H) {
      layer.width = W;
      layer.height = H;
      drawn.current.upTo = -1;
    }
    const lctx = layer.getContext('2d');
    const ctx = canvas.getContext('2d');
    if (!lctx || !ctx) return;
    const key = `${v.zoom}|${v.panX}|${v.panY}|${W}|${H}|${showJumps}|${showImage}|${imgEl ? 1 : 0}`;
    if (!seq) {
      clearCanvas(ctx, W, H, FABRIC);
      return;
    }
    if (drawn.current.key !== key || pos < drawn.current.upTo || drawn.current.upTo < 0) {
      clearCanvas(lctx, W, H, FABRIC);
      if (showImage && imgEl && design.backdrop) {
        setViewTransform(lctx, v, dpr);
        lctx.globalAlpha = Math.min(0.35, design.backdrop.opacity);
        lctx.drawImage(imgEl, design.backdrop.x, design.backdrop.y, design.backdrop.widthMm, design.backdrop.heightMm);
        lctx.globalAlpha = 1;
      }
      drawStitchPaths(lctx, buildStitchPaths(seq, pos), v, dpr, { showJumps });
    } else if (pos > drawn.current.upTo) {
      drawRange(lctx, seq, Math.max(0, drawn.current.upTo - 1), pos, v, dpr, showJumps);
    }
    drawn.current = { upTo: pos, key };
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(layer, 0, 0);
    // needle marker at the current position
    const i = Math.min(seq.count - 1, Math.max(0, pos - 1));
    if (seq.count) {
      const x = (seq.xs[i] * v.zoom + v.panX) * dpr, y = (seq.ys[i] * v.zoom + v.panY) * dpr;
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 1.5 * dpr;
      ctx.beginPath();
      ctx.arc(x, y, 6 * dpr, 0, Math.PI * 2);
      ctx.moveTo(x - 11 * dpr, y);
      ctx.lineTo(x + 11 * dpr, y);
      ctx.moveTo(x, y - 11 * dpr);
      ctx.lineTo(x, y + 11 * dpr);
      ctx.stroke();
    }
  }, [seq, pos, v, size, showJumps, showImage, imgEl, design.backdrop]);

  const step = useCallback((d: number) => {
    if (!seq) return;
    setPlaying(false);
    setPos((p) => Math.max(0, Math.min(seq.count, p + d)));
  }, [seq]);

  // keyboard: space, arrows, home/end
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (t instanceof Element && t.closest('input, select, textarea')) return;
      if (e.key === ' ') {
        e.preventDefault();
        if (seq && pos >= seq.count) setPos(0);
        setPlaying((p) => !p);
      } else if (e.key === 'ArrowRight') step(e.shiftKey ? 100 : 1);
      else if (e.key === 'ArrowLeft') step(e.shiftKey ? -100 : -1);
      else if (e.key === 'Home') step(-Infinity);
      else if (e.key === 'End') step(Infinity);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [seq, pos, step]);

  const onWheel = (e: React.WheelEvent) => {
    const r = stageRef.current!.getBoundingClientRect();
    const factor = wheelZoomFactor(e);
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    store.set((st) => ({ viewport: zoomAt(st.viewport, factor, mx, my) }));
  };

  // prevent page scroll/zoom while zooming the canvas
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const prevent = (e: WheelEvent) => e.preventDefault();
    el.addEventListener('wheel', prevent, { passive: false });
    return () => el.removeEventListener('wheel', prevent);
  }, [seq]);
  const panStart = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const [touch] = useState(() => new TouchGesture());
  const stageOrigin = () => {
    const r = stageRef.current!.getBoundingClientRect();
    return { x: r.left, y: r.top };
  };

  if (!seq || !timeline) {
    return (
      <div className="stage" ref={stageRef}>
        <div className="stage-empty">
          <div className="card">{pending ? t('Generating stitches…') : t('No stitches to show. Add objects first.')}</div>
        </div>
      </div>
    );
  }

  const i = Math.min(seq.count - 1, Math.max(0, pos - 1));
  const block = seq.blocks[i];
  const thread = seq.threads[block];
  const objIndex = seq.objs[i];
  const obj = objIndex >= 0 ? design.objects[objIndex] : null;
  const sewn = timeline.stitchesBefore[pos];
  const total = timeline.stitchesBefore[seq.count];
  const remainingSeconds = result ? (result.stats.estimatedSeconds * (total - sewn)) / Math.max(1, total) : 0;

  return (
    <div
      className="stage"
      ref={stageRef}
      onWheel={onWheel}
      onPointerDown={(e) => {
        if ((e.target as Element).closest('.sim-controls')) return;
        (e.target as Element).setPointerCapture(e.pointerId);
        if (touch.down(e, stageOrigin(), v)) {
          panStart.current = null;
          return;
        }
        panStart.current = { x: e.clientX, y: e.clientY, px: v.panX, py: v.panY };
      }}
      onPointerMove={(e) => {
        const pinched = touch.move(e, stageOrigin());
        if (pinched) store.set({ viewport: pinched });
        if (touch.active) return;
        const p = panStart.current;
        if (p) store.set({ viewport: { ...v, panX: p.px + e.clientX - p.x, panY: p.py + e.clientY - p.y } });
      }}
      onPointerUp={(e) => {
        touch.up(e);
        panStart.current = null;
      }}
      onPointerCancel={(e) => {
        touch.up(e);
        panStart.current = null;
      }}
      data-testid="simulator"
    >
      <canvas ref={canvasRef} aria-label={t('Stitch simulation')} role="img" style={{ cursor: 'grab' }} />
      <div className="overlay-tl">
        {pending && <span className="chip busy">{t('Updating stitches…')}</span>}
        <span className="legend chip">
          <span><i style={{ borderColor: thread?.hex ?? '#888' }} />{t('stitch')}</span>
          <span><i style={{ borderColor: '#d62828', borderTopStyle: 'dashed' }} />{t('jump')}</span>
          <span style={{ color: '#d62828' }}>✕ {t('trim')}</span>
          <span>▮ {t('colour change')}</span>
        </span>
        <ZoomControls stage={() => stageRef.current} onFit={() => zoomToFit(size.w, size.h - 110)} />
      </div>
      <div className="sim-controls">
        <div className="row">
          <button className="btn icon" aria-label={t('To start')} onClick={() => step(-Infinity)}>
            <IconStepBack size={14} />
          </button>
          <button className="btn small" onClick={() => step(-100)} aria-label={t('100 stitches back')}>−100</button>
          <button className="btn small" onClick={() => step(-1)} aria-label={t('One stitch back')}>−1</button>
          <button
            className="btn primary icon"
            aria-label={playing ? t('Pause') : t('Play')}
            onClick={() => {
              if (pos >= seq.count) setPos(0);
              setPlaying(!playing);
            }}
          >
            {playing ? <IconPause size={14} /> : <IconPlay size={14} />}
          </button>
          <button className="btn small" onClick={() => step(1)} aria-label={t('One stitch forward')}>+1</button>
          <button className="btn small" onClick={() => step(100)} aria-label={t('100 stitches forward')}>+100</button>
          <button className="btn icon" aria-label={t('To end')} onClick={() => step(Infinity)}>
            <IconStepFwd size={14} />
          </button>
          <label className="row" style={{ marginLeft: 8 }}>
            <span className="hint" style={{ margin: 0 }}>{t('Speed')}</span>
            <select className="input" style={{ width: 120 }} value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
              {SPEEDS.map((s) => (
                <option key={s} value={s}>
                  {t('{n} cmd/s', { n: fmtInt(s) })}
                </option>
              ))}
            </select>
          </label>
          <label className="check" style={{ marginLeft: 8 }}>
            <input type="checkbox" checked={showJumps} onChange={(e) => setShowJumps(e.target.checked)} /> {t('Show jumps and trims')}
          </label>
        </div>
        <div className="timeline" aria-label={t('Timeline')}>
          {timeline.blocks.map((b, k) => (
            <div key={k} className="blk" style={{ left: `${(b.start / seq.count) * 100}%`, width: `${((b.end - b.start) / seq.count) * 100}%`, background: b.color }} />
          ))}
          {timeline.changes.map((c) => (
            <div key={'c' + c} className="mark" style={{ left: `${(c / seq.count) * 100}%`, width: 3, background: '#000' }} />
          ))}
          {timeline.trims.length < 400 && timeline.trims.map((t) => <div key={'t' + t} className="mark" style={{ left: `${(t / seq.count) * 100}%` }} />)}
          <div className="head" style={{ left: `calc(${(pos / seq.count) * 100}% - 1px)` }} />
          <input
            type="range"
            min={0}
            max={seq.count}
            value={pos}
            aria-label={t('Stitch position')}
            aria-valuetext={t('Command {n} of {total}', { n: pos, total: seq.count })}
            onChange={(e) => {
              setPlaying(false);
              setPos(Number(e.target.value));
            }}
          />
        </div>
        {/* fixed columns + padded numbers: nothing moves while the simulation plays */}
        <div className="sim-info" aria-live="off">
          <span>
            {t('Stitches')} <b>{pad(fmtInt(sewn), fmtInt(total).length)}</b> / {fmtInt(total)}
          </span>
          <span>
            {t('Command')} <b>{t(CMD_NAMES[seq.cmds[i]])}</b>
          </span>
          <span title={thread?.name}>
            {t('Colour')} <b>{block + 1}</b>/{seq.threads.length} <span className="swatch" style={{ background: thread?.hex, display: 'inline-block', verticalAlign: 'middle' }} /> {thread?.name}
          </span>
          <span title={obj ? `${objIndex + 1}. ${obj.name}` : undefined}>
            {t('Object')} <b>{obj ? `${objIndex + 1}. ${obj.name}` : '—'}</b>
          </span>
          <span>
            {t('Position')} <b>{pad(seq.xs[i].toFixed(1), 6)}, {pad(seq.ys[i].toFixed(1), 6)}</b> mm
          </span>
          <span>{t('Remaining ≈ {time} (estimate)', { time: formatDuration(remainingSeconds) })}</span>
        </div>
      </div>
    </div>
  );
}

/** Left-pads with figure spaces (same width as digits) so numbers keep a constant width. */
const pad = (text: string, width: number): string => ' '.repeat(Math.max(0, width - text.length)) + text;

/** Draws commands [from, to) onto an existing layer. */
function drawRange(ctx: CanvasRenderingContext2D, seq: StitchSequence, from: number, to: number, v: { zoom: number; panX: number; panY: number }, dpr: number, showJumps: boolean): void {
  setViewTransform(ctx, v, dpr);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const w = Math.max(THREAD_WIDTH_MM, 1 / v.zoom);
  let color = '';
  ctx.beginPath();
  const flush = () => {
    if (color) {
      ctx.strokeStyle = color;
      ctx.lineWidth = w;
      ctx.stroke();
    }
    ctx.beginPath();
  };
  for (let i = Math.max(1, from); i < to; i++) {
    const c = seq.cmds[i], pc = seq.cmds[i - 1];
    if (c === CMD.STITCH && pc === CMD.STITCH) {
      const col = seq.threads[seq.blocks[i]]?.hex ?? '#888';
      if (col !== color) {
        flush();
        color = col;
      }
      ctx.moveTo(seq.xs[i - 1], seq.ys[i - 1]);
      ctx.lineTo(seq.xs[i], seq.ys[i]);
    } else if (c === CMD.JUMP && showJumps) {
      flush();
      ctx.setLineDash([4 / v.zoom, 3 / v.zoom]);
      ctx.strokeStyle = '#d62828';
      ctx.lineWidth = 1 / v.zoom;
      ctx.moveTo(seq.xs[i - 1], seq.ys[i - 1]);
      ctx.lineTo(seq.xs[i], seq.ys[i]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
    } else if (c === CMD.TRIM && showJumps) {
      flush();
      const r = 3 / v.zoom;
      ctx.strokeStyle = '#d62828';
      ctx.lineWidth = 1 / v.zoom;
      ctx.moveTo(seq.xs[i] - r, seq.ys[i] - r);
      ctx.lineTo(seq.xs[i] + r, seq.ys[i] + r);
      ctx.moveTo(seq.xs[i] - r, seq.ys[i] + r);
      ctx.lineTo(seq.xs[i] + r, seq.ys[i] - r);
      ctx.stroke();
      ctx.beginPath();
    }
  }
  flush();
}
